import React from 'react';
import { Button, Tag } from 'antd';
import { EyeOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { usePermissions } from '../../../contexts/PermissionContext';
import MobileTable from '../../../Component/MobileTable';

interface InvoicesTableProps {
  invoices: any[];
  loading: boolean;
  currentPage: number;
  pageSize: number;
  total: number;
  onPageChange: (page: number) => void;
}

const InvoicesTable: React.FC<InvoicesTableProps> = ({
  invoices,
  loading,
  currentPage,
  pageSize,
  total,
  onPageChange,
}) => {
  const { t } = useTranslation();
  const { hasPermission } = usePermissions();
  const history = useHistory();

  const handleViewInvoice = (invoiceId: number) => {
    history.push(`/invoices/${invoiceId}`);
  };

  const invoiceColumns = [
    {
      title: t('invoices.columns.reference', 'Reference'),
      dataIndex: 'reference',
      key: 'reference',
    },
    {
      title: t('invoices.columns.issueDate', 'Issue Date'),
      dataIndex: 'issue_date',
      key: 'issue_date',
      render: (date: string) => new Date(date).toLocaleDateString(),
    },
    {
      title: t('invoices.columns.value', 'Value'),
      dataIndex: 'total',
      key: 'total',
      render: (value: any) => {
        const numValue = parseFloat(value);
        return isNaN(numValue) ? '0.00 ر.س' : `${numValue.toFixed(2)} ر.س`;
      },
    },
    {
      title: t('invoices.columns.status', 'Status'),
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => <Tag color={status === 'قبض' ? 'green' : 'orange'}>{status}</Tag>,
    },
    {
      title: t('invoices.columns.actions', 'Actions'),
      key: 'actions',
      width: 80,
      render: (_: any, record: any) => {
        const canViewInvoice =
          hasPermission('view_invoice') ||
          hasPermission('invoices.read') ||
          hasPermission('invoices.view') ||
          hasPermission('customers.view_invoices');

        if (!canViewInvoice) {
          return null;
        }

        return (
          <Button
            type="link"
            size="small"
            icon={<EyeOutlined />}
            onClick={() => handleViewInvoice(record.id)}
          >
            {t('common.view', 'View')}
          </Button>
        );
      },
    },
  ];

  return (
    <MobileTable
      columns={invoiceColumns}
      dataSource={invoices}
      rowKey="id"
      loading={loading}
      onRowClick={(record: any) => handleViewInvoice(record.id)}
      pagination={{
        current: currentPage,
        pageSize: pageSize,
        total: total,
        onChange: onPageChange,
        showSizeChanger: false,
        showQuickJumper: true,
        showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
      }}
      scroll={{ x: 800 }}
    />
  );
};

export default InvoicesTable;
